import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { AuthService } from './services/auth.service';
import { ProfileService } from './services/profile.service';

/** Resolve WHO is signed in before the first route is decided.
 *
 *  The CMS used to learn that an account is org staff from the sign-in
 *  response alone and keep it in localStorage. That made one cache key
 *  load-bearing: lose it and an org owner was silently demoted to the
 *  platform tier, with a dashboard the API 403s and no route back into
 *  their own console. `/admin/users/me` knows on every request, so the
 *  cache is now just a cache and this is the truth.
 *
 *  Costs nothing: the header fetches the same profile on every page, and
 *  ProfileService caches it per account, so this replaces that call
 *  rather than adding one. A failure is not fatal: the guards fall back
 *  to whatever the cache holds and the first real API call decides.
 */
export async function hydrateSession(): Promise<void> {
  const auth = inject(AuthService);
  const profile = inject(ProfileService);
  if (!auth.hasLiveSession()) return;
  try {
    const me = await firstValueFrom(profile.refresh());
    auth.adoptOrgContext(me?.orgContext ?? null);
    auth.adoptRoles(me?.roleIds);
  } catch {
    // Offline, 401, a server having a bad day: boot anyway.
  }
}
